import { Component, Input, OnInit, DoCheck, OnDestroy } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { combineLatest, mergeMap, Observable, of, Subscription } from 'rxjs';
import { take } from 'rxjs/operators'; // <-- IMPORTANTE
import Swal from 'sweetalert2';
import { RefundService } from '@app/shared/services';
import {
  Credentials,
  CredentialsService,
} from '../../../auth/credentials.service';
import { environment } from '@env/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { InstructionModalComponent } from './instruction-modal/instruction-modal.component';

@Component({
  selector: 'app-step2-support',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InstructionModalComponent],
  templateUrl: './step2-support.component.html',
  styleUrl: './step2-support.component.scss',
})
export class Step2SupportComponent implements OnInit, DoCheck, OnDestroy {
  public loading: boolean = false;

  @Input() public form?: FormGroup | any;
  @Input() public formPrescriptions?: FormGroup | any;
  @Input() public stepper!: any;

  public assistType: 'medical' | 'travel' = 'medical';
  public hasPreviousDocuments: boolean = false;
  passport: File[] = [];
  expenses: Array<File[]> = [];
  prescriptions: Array<File[]> = [];
  specialSupport: File[] = [];
  otherDocuments: File[] = [];

  modalData: {
    type: string;
    index: number;
    title: string;
    max: number;
    icon: string;
  } | null = null;
  activeModalType: 'instructions' | 'upload' | null = null;

  private lastFormLength: number = -1;
  private dbExpenses: any[] = [];

  private subs: Subscription = new Subscription();

  get expenseForm(): FormArray | null {
    return this.form ? (this.form.get('expenses') as FormArray) : null;
  }

  get expenseControls() {
    return this.expenseForm ? this.expenseForm.controls : [];
  }

  get currentUser(): Credentials | null {
    return this._credential.credentials;
  }

  constructor(
    private _refound: RefundService,
    private _credential: CredentialsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const idAssist =
      this.route.parent?.snapshot.params['id'] ||
      this.route.snapshot.params['id'];

    this.subs.add(
      this._refound.assistance$
        .pipe(
          mergeMap((resp) =>
            resp
              ? of(resp)
              : this._refound.getAssistanceById(
                  idAssist,
                  this.currentUser?.crmObjId ?? '',
                ),
          ),
        )
        .subscribe({
          next: (resp: any) => {
            if (resp) {
              this.assistType =
                resp.assistType === environment.assistType.medical
                  ? 'medical'
                  : 'travel';

              if (
                resp.assistancedocument &&
                resp.assistancedocument.length > 0
              ) {
                const doc = resp.assistancedocument[0];

                this.passport = [];
                this.specialSupport = [];
                this.otherDocuments = [];

                const p1 = this.createDbFile(doc.urlPassport);
                const p2 = this.createDbFile(doc.urlPassport2);
                if (p1) this.passport.push(p1);
                if (p2) this.passport.push(p2);

                const processMixedUrl = (urlStr: string) => {
                  if (!urlStr) return;
                  urlStr
                    .split(' ')
                    .filter(Boolean)
                    .forEach((u) => {
                      const f = this.createDbFile(u);
                      if (f) {
                        if (u.includes('/MED-') || u.includes('/AIR-'))
                          this.specialSupport.push(f);
                        else if (u.includes('/OTH-'))
                          this.otherDocuments.push(f);
                      }
                    });
                };

                processMixedUrl(doc.urlOthers);
                processMixedUrl(doc.urlOthers2);
                processMixedUrl(doc.urlOthers3);
              }

              if (resp.expenseassistence && resp.expenseassistence.length > 0) {
                this.dbExpenses = resp.expenseassistence;
                this.populateExpenseFiles();
              }
            }
          },
          error: () => console.log('Error recuperando asistencia'),
        }),
    );
  }

  ngDoCheck(): void {
    const currentLength = this.expenseControls.length;
    if (currentLength !== this.lastFormLength) {
      this.lastFormLength = currentLength;

      while (this.expenses.length < currentLength) this.expenses.push([]);
      while (this.expenses.length > currentLength) this.expenses.pop();

      while (this.prescriptions.length < currentLength)
        this.prescriptions.push([]);
      while (this.prescriptions.length > currentLength)
        this.prescriptions.pop();

      this.populateExpenseFiles();
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this.subs.unsubscribe();
  }

  private populateExpenseFiles() {
    if (!this.dbExpenses || this.dbExpenses.length === 0) return;

    this.expenseControls.forEach((ctrl, index) => {
      if (this.expenses[index] && this.expenses[index].length > 0) return;

      const idExp = ctrl.getRawValue().id;
      const dbExp = this.dbExpenses.find((e: any) => e.idExpense === idExp);

      if (dbExp && dbExp.expensedocument && dbExp.expensedocument.length > 0) {
        const doc = dbExp.expensedocument[0];

        const processExpUrl = (urlStr: string) => {
          if (!urlStr) return;
          urlStr
            .split(' ')
            .filter(Boolean)
            .forEach((u) => {
              const f = this.createDbFile(u);
              if (f) {
                if (u.includes('/EXP-')) this.expenses[index].push(f);
                else if (u.includes('/PRE-')) this.prescriptions[index].push(f);
              }
            });
        };

        processExpUrl(doc.urlDocumentExpense);
        processExpUrl(doc.urlDocumentExpense2);
        processExpUrl(doc.urlDocumentExpense3);
      }
    });
  }

  private createDbFile(url: string): any {
    if (!url || url.trim() === '') return null;
    const match = url.match(/storage\/([^\/]+)/);
    const name = match ? match[1] : 'documento_previo.pdf';
    const file: any = new File([''], name, { type: 'application/pdf' });
    file.isDbFile = true;
    file.dbUrl = url;
    return file;
  }

  getTypeExpense(index: number): string {
    const ctrl = this.expenseControls[index];
    return ctrl ? ctrl.getRawValue().typeExpense : '';
  }

  getIdExpense(index: number): any {
    const ctrl = this.expenseControls[index];
    return ctrl ? ctrl.getRawValue().id : null;
  }

  getExpensesFiles(index: number): File[] {
    if (!this.expenses[index]) this.expenses[index] = [];
    return this.expenses[index];
  }

  getPrescriptionsFiles(index: number): File[] {
    if (!this.prescriptions[index]) this.prescriptions[index] = [];
    return this.prescriptions[index];
  }

  isPassportValid(): boolean {
    return this.passport.length === 2;
  }
  isExpenseValid(index: number): boolean {
    return this.getExpensesFiles(index).length > 0;
  }
  isPrescriptionValid(index: number): boolean {
    return this.getPrescriptionsFiles(index).length > 0;
  }
  isSpecialSupportValid(): boolean {
    return this.specialSupport.length > 0;
  }
  isOtherValid(): boolean {
    return this.otherDocuments.length > 0;
  }

  isMedicineExpense(typeExpense: string): boolean {
    if (!typeExpense) return false;
    const type = typeExpense.toLowerCase();
    return (
      type.includes('medicina') ||
      type.includes('medicamento') ||
      type.includes('medicinas')
    );
  }

  openModal(
    type: string,
    index: number,
    title: string,
    max: number,
    icon: string,
  ) {
    this.modalData = { type, index, title, max, icon };
    const currentFiles = this.getActiveFiles();

    if (currentFiles.length === 0) {
      this.activeModalType = 'instructions';
    } else {
      this.activeModalType = 'upload';
    }
    document.body.style.overflow = 'hidden';
  }

  proceedToUpload() {
    this.activeModalType = 'upload';
  }

  closeModal() {
    this.modalData = null;
    this.activeModalType = null;
    document.body.style.overflow = 'auto';
  }

  getActiveFiles(): File[] {
    if (!this.modalData) return [];
    switch (this.modalData.type) {
      case 'passport':
        return this.passport;
      case 'expense':
        return this.getExpensesFiles(this.modalData.index);
      case 'prescription':
        return this.getPrescriptionsFiles(this.modalData.index);
      case 'specialSupport':
        return this.specialSupport;
      case 'other':
        return this.otherDocuments;
      default:
        return [];
    }
  }

  getModalDescription(): string {
    if (!this.modalData) return '';
    switch (this.modalData.type) {
      case 'passport':
        return 'Recuerda que debes de cargar dos (2) imágenes/documentos del pasaporte.';
      case 'expense':
        return 'Adjunta las facturas y el comprobante de pago correspondientes a los gastos realizados.';
      case 'prescription':
        return 'Carga las recetas médicas con las que cuentes, deben ser claras y legibles.';
      case 'specialSupport':
        return this.assistType === 'medical'
          ? 'Carga tu informe médico en el sistema.'
          : 'Adjunta los soportes que respalden tu solicitud, como carta de la aerolínea, denuncia de pérdida, entre otros.';
      case 'other':
        return 'Recuerda que deben ser claros y completamente legibles. (Máximo 5 páginas)';
      default:
        return '';
    }
  }

  removeActiveFile(file: File) {
    if (!this.modalData) return;
    switch (this.modalData.type) {
      case 'passport':
        this.passport.splice(this.passport.indexOf(file), 1);
        break;
      case 'expense':
        this.expenses[this.modalData.index].splice(
          this.expenses[this.modalData.index].indexOf(file),
          1,
        );
        break;
      case 'prescription':
        this.prescriptions[this.modalData.index].splice(
          this.prescriptions[this.modalData.index].indexOf(file),
          1,
        );
        break;
      case 'specialSupport':
        this.specialSupport.splice(this.specialSupport.indexOf(file), 1);
        break;
      case 'other':
        this.otherDocuments.splice(this.otherDocuments.indexOf(file), 1);
        break;
    }
  }

  onNativeFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0 || !this.modalData) return;

    const addedFiles = Array.from(input.files);
    const mockEvent = { addedFiles };

    switch (this.modalData.type) {
      case 'passport':
        this.onSelectArray(mockEvent, this.passport, 2, 'pasaporte');
        break;
      case 'expense':
        this.onSelectArray(
          mockEvent,
          this.expenses[this.modalData.index],
          3,
          'gasto',
        );
        break;
      case 'prescription':
        this.onSelectArray(
          mockEvent,
          this.prescriptions[this.modalData.index],
          3,
          'receta médica',
        );
        break;
      case 'specialSupport':
        this.onSelectArray(
          mockEvent,
          this.specialSupport,
          3,
          this.assistType === 'medical' ? 'informe médico' : 'soportes',
        );
        break;
      case 'other':
        this.onSelectArray(
          mockEvent,
          this.otherDocuments,
          3,
          'otros documentos',
        );
        break;
    }
    input.value = '';
  }

  onSelectArray(
    event: { addedFiles: any },
    targetArray: File[],
    maxLimit: number,
    itemName: string,
  ) {
    if (targetArray.length + event.addedFiles.length <= maxLimit) {
      targetArray.push(...event.addedFiles);
    } else {
      Swal.fire({
        icon: 'error',
        text: `Solo puedes cargar máximo ${maxLimit} archivos para ${itemName}.`,
        timer: 2000,
      });
    }
  }

  getFileExtension(filename: string) {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  }

  sincronizarDocumentosAlServicio(): void {
    const documentos: any[] = [];
    this.passport.forEach((a) =>
      documentos.push({ tipoDocumento: 'PASAPORTE/OTROS', nombre: a.name }),
    );
    this.specialSupport.forEach((a) => {
      const tipo =
        this.assistType === 'medical' ? 'INFORME MÉDICO' : 'SOPORTES';
      documentos.push({ tipoDocumento: tipo, nombre: a.name });
    });
    this.otherDocuments.forEach((a) =>
      documentos.push({ tipoDocumento: 'OTROS DOCUMENTOS', nombre: a.name }),
    );

    for (let i = 0; i < this.expenseControls.length; i++) {
      this.getExpensesFiles(i).forEach((a) =>
        documentos.push({ tipoDocumento: 'FACTURA DE GASTOS', nombre: a.name }),
      );
      this.getPrescriptionsFiles(i).forEach((a) =>
        documentos.push({ tipoDocumento: 'RECETA MÉDICA', nombre: a.name }),
      );
    }
    this._refound.documentosCargados = documentos;
  }

  onSubmit() {
    if (this.hasPreviousDocuments) {
      this.sincronizarDocumentosAlServicio();
      this.stepper?.next();
      return;
    }

    if (!this.isPassportValid()) {
      Swal.fire({
        icon: 'error',
        text: 'Debes cargar tu pasaporte por ambos lados (exactamente 2 archivos).',
        timer: 2000,
      });
      return;
    }

    if (!this.isSpecialSupportValid()) {
      Swal.fire({
        icon: 'error',
        text: `Debes cargar ${this.assistType === 'medical' ? 'el informe médico' : 'los soportes correspondientes'}.`,
        timer: 2000,
      });
      return;
    }

    const controls = this.expenseControls;
    for (let i = 0; i < controls.length; i++) {
      if (!this.getIdExpense(i)) {
        Swal.fire({
          icon: 'error',
          title: 'Error de Sincronización',
          text: `El gasto "${this.getTypeExpense(i)}" no tiene un ID asignado. Por favor, regresa al Paso 1, asegúrate de guardar los gastos y vuelve a intentar.`,
        });
        return;
      }

      if (!this.isExpenseValid(i)) {
        Swal.fire({
          icon: 'error',
          text: 'Asegúrate de que todas las facturas de gastos contengan un documento.',
          timer: 2000,
        });
        return;
      }

      if (
        this.isMedicineExpense(this.getTypeExpense(i)) &&
        !this.isPrescriptionValid(i)
      ) {
        Swal.fire({
          icon: 'error',
          text: 'Asegúrate de cargar la receta médica correspondiente.',
          timer: 2000,
        });
        return;
      }
    }

    this.loading = true;
    const userId = this.currentUser?.id ?? 1;
    const quoteId = this.currentUser?.crmObjId ?? '1';
    const anio = new Date().getFullYear();
    const mes = new Date().getMonth() + 1;
    const dia = new Date().getDate();

    this._refound.assistance$.pipe(take(1)).subscribe((assistance: any) => {
      const uploadGlobalFile = (file: File | any, prefix: string) => {
        if (!file) return of('');
        if (file.isDbFile) {
          console.log(
            `[ARCHIVO VIRTUAL] ${prefix}: Recuperado de DB ->`,
            file.dbUrl,
          );
          return of(file.dbUrl);
        }
        const fileName = `${prefix}-${quoteId}-${anio}${mes}${dia}-${Math.floor(Math.random() * 1000)}.${this.getFileExtension(file.name)}`;
        console.log(`[SUBIENDO ARCHIVO] ${prefix}:`, fileName);
        return this._refound.uploadFile(fileName, quoteId, userId, file);
      };

      const UrlPassport$ = uploadGlobalFile(this.passport[0], 'PAS');
      const UrlPassport2$ = uploadGlobalFile(this.passport[1], 'PAS');

      const spPrefix = this.assistType === 'medical' ? 'MED' : 'AIR';
      const UrlSpecial1$ = uploadGlobalFile(this.specialSupport[0], spPrefix);
      const UrlSpecial2$ = uploadGlobalFile(this.specialSupport[1], spPrefix);
      const UrlSpecial3$ = uploadGlobalFile(this.specialSupport[2], spPrefix);

      const UrlOthers1$ = uploadGlobalFile(this.otherDocuments[0], 'OTH');
      const UrlOthers2$ = uploadGlobalFile(this.otherDocuments[1], 'OTH');
      const UrlOthers3$ = uploadGlobalFile(this.otherDocuments[2], 'OTH');

      const document$ = combineLatest([
        UrlPassport$,
        UrlPassport2$,
        UrlSpecial1$,
        UrlSpecial2$,
        UrlSpecial3$,
        UrlOthers1$,
        UrlOthers2$,
        UrlOthers3$,
      ]).pipe(
        mergeMap(([UrlPassport, UrlPassport2, s1, s2, s3, o1, o2, o3]) => {
          const payloadDocumentos = {
            IdAssistance: assistance.idAssistence,
            DateCreate: new Date().toJSON(),
            NumberCase: assistance.numberCase,
            UrlPassport: UrlPassport ?? '',
            UrlPassport2: UrlPassport2 ?? '',
            UrlOthers: [s1, o1].filter(Boolean).join(' '),
            UrlOthers2: [s2, o2].filter(Boolean).join(' '),
            UrlOthers3: [s3, o3].filter(Boolean).join(' '),
          };
          console.log('[PAYLOAD ASISTENCIA]', payloadDocumentos);
          return this._refound.sendAsistanceDocument(payloadDocumentos);
        }),
      );

      const expenses$: Observable<Object>[] = controls.map(
        (_, index: number) => {
          const typeExp = this.getTypeExpense(index);
          const idExp = this.getIdExpense(index);
          const expFiles = this.expenses[index];
          const preFiles = this.prescriptions[index] || [];

          const UrlExpense$ = uploadGlobalFile(expFiles[0], 'EXP');
          const UrlExpense2$ = uploadGlobalFile(expFiles[1], 'EXP');
          const UrlExpense3$ = uploadGlobalFile(expFiles[2], 'EXP');

          if (this.isMedicineExpense(typeExp)) {
            const UrlPre$ = uploadGlobalFile(preFiles[0], 'PRE');
            const UrlPre2$ = uploadGlobalFile(preFiles[1], 'PRE');
            const UrlPre3$ = uploadGlobalFile(preFiles[2], 'PRE');

            return combineLatest([
              UrlExpense$,
              UrlExpense2$,
              UrlExpense3$,
              UrlPre$,
              UrlPre2$,
              UrlPre3$,
            ]).pipe(
              mergeMap(
                ([
                  UrlExpense,
                  UrlExpense2,
                  UrlExpense3,
                  UrlPre,
                  UrlPre2,
                  UrlPre3,
                ]) => {
                  const payloadGastoMedico = {
                    IdExpense: idExp,
                    FechCreate: new Date().toJSON(),
                    UrlDocumentExpense: [UrlExpense, UrlPre]
                      .filter(Boolean)
                      .join(' '),
                    UrlDocumentExpense2: [UrlExpense2, UrlPre2]
                      .filter(Boolean)
                      .join(' '),
                    UrlDocumentExpense3: [UrlExpense3, UrlPre3]
                      .filter(Boolean)
                      .join(' '),
                  };
                  console.log(
                    `[PAYLOAD GASTO MEDICO ID: ${idExp}]`,
                    payloadGastoMedico,
                  );
                  return this._refound.sendExpenseDocument(payloadGastoMedico);
                },
              ),
            );
          } else {
            return combineLatest([
              UrlExpense$,
              UrlExpense2$,
              UrlExpense3$,
            ]).pipe(
              mergeMap(([UrlExpense, UrlExpense2, UrlExpense3]) => {
                const payloadGasto = {
                  IdExpense: idExp,
                  FechCreate: new Date().toJSON(),
                  UrlDocumentExpense: UrlExpense ?? '',
                  UrlDocumentExpense2: UrlExpense2 ?? '',
                  UrlDocumentExpense3: UrlExpense3 ?? '',
                };
                console.log(
                  `[PAYLOAD GASTO NORMAL ID: ${idExp}]`,
                  payloadGasto,
                );
                return this._refound.sendExpenseDocument(payloadGasto);
              }),
            );
          }
        },
      );

      combineLatest([document$, ...expenses$]).subscribe({
        next: () => {
          console.log(
            '[EXITO] Todos los documentos y gastos guardados en BD exitosamente.',
          );
          this.loading = false;
          this.sendToSalesforce(assistance.numberCase);
          this.sincronizarDocumentosAlServicio();
          this.stepper?.next();
        },
        error: (err) => {
          console.error(
            '[ERROR COMBINELATEST] Hubo un error guardando en BD:',
            err,
          );
          this.loading = false;
          Swal.fire(
            'Error',
            'Hubo un error guardando los archivos, inténtalo de nuevo.',
            'error',
          );
        },
      });
    });
  }

  sendToSalesforce(id: string) {
    const body: any = { idAssistance: id };

    this._refound.sendDocumentsSalesforce(body).subscribe({
      next: () => {
        console.log('Documentos sincronizados exitosamente.');
      },
      error: (err) => {
        const backendError =
          err.error?.message ||
          err.error?.Message ||
          err.error?.title ||
          JSON.stringify(err.error);
        Swal.fire({
          icon: 'error',
          title: 'Error de validación',
          text: `Salesforce rechazó la petición. Detalle: ${backendError}`,
          confirmButtonColor: '#C00A27',
        });
      },
    });
  }
}
